import { useEffect, useState } from "react";
import * as api from "./api";

const CONNECTOR_INFO_BY_TYPE = {
    button: "Створити відповідь кнопкою",
    text: "Створити відповідь текстом",
};

const DEFAULT_FORM_ENTITIES = {
    title: { visible: true, name: "Заголовок" },
    text: { visible: true, name: "Текст" },
    isRight: { visible: true, name: "Вірність" },
    points: { visible: false, name: "Бали" },
    estimationGroup: { visible: false, name: "Група оцінки" },
    stage: { visible: false, name: "Етап" },
};

// Owns the "add answer connector" form state plus the mutation handlers
// (save connector, delete connector, delete question) for a single question
// node on the canvas.
export default function useConnectorForm({ item, choseNextQuestionMode, setChoseNextQuestionMode, gotUpdatedQuestion }) {
    const [isVisibleAddConnectorOptions, setIsVisibleAddConnectorOptions] = useState(false);

    const [inputValue, setInputValue] = useState("");
    const [isRightValue, setIsRightValue] = useState(null);
    const [pointValue, setPointValue] = useState(0);
    const [estimationGroupValue, setEstimationGroupValue] = useState(null);
    const [connectorTypeChosen, setConnectorTypeChosen] = useState(null);
    const [stageValue, setStageValue] = useState(null);
    const [wordValue, setWordValue] = useState("");
    const [isWordPickerOpen, setIsWordPickerOpen] = useState(false);

    const [addConnectorInfo, setAddConnectorInfo] = useState(CONNECTOR_INFO_BY_TYPE.button);
    const [isVisibleChangeConnectorButton, setIsVisibleChangeConnectorButton] = useState(false);
    const [connectorFormEntities, setConnectorFormEntities] = useState(DEFAULT_FORM_ENTITIES);
    const [connectorConfig, setConnectorConfig] = useState(false);

    const [connectorFunctionsToShowId, setConnectorFunctionsToShowId] = useState(null);
    const [hintConnector, setHintConnector] = useState();

    useEffect(() => {
        if (inputValue && choseNextQuestionMode && choseNextQuestionMode.questionIdFrom === item.id) {
            setIsVisibleChangeConnectorButton(true);
        } else {
            setIsVisibleChangeConnectorButton(false);
        }
        // Mirrors the original: only reacts to choseNextQuestionMode changing
        // (typing itself is handled directly in inputChangeHandler below).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [choseNextQuestionMode]);

    const resetHintState = () => {
        setConnectorFunctionsToShowId(null);
        setHintConnector(undefined);
    };

    const afterConnectorSaved = () => {
        setConnectorConfig(false);
        setInputValue("");
        setIsRightValue(null);
        setPointValue(0);
        setEstimationGroupValue(null);
        setConnectorTypeChosen(null);
        setAddConnectorInfo(CONNECTOR_INFO_BY_TYPE.button);
        setStageValue(null);
        setWordValue("");
        setIsWordPickerOpen(false);
        setIsVisibleAddConnectorOptions(false);
        setChoseNextQuestionMode(null);
        return gotUpdatedQuestion();
    };

    const afterMutation = () => {
        resetHintState();
        return gotUpdatedQuestion();
    };

    const saveConnector = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!inputValue || !choseNextQuestionMode.questionIdNext || !choseNextQuestionMode.questionIdFrom) return;

        api.saveConnector({
            text: inputValue,
            fromId: choseNextQuestionMode.questionIdFrom,
            nextId: choseNextQuestionMode.questionIdNext,
            isRight: isRightValue,
            clasification: connectorTypeChosen,
            estimationGroup: estimationGroupValue,
            point: pointValue,
            stage: stageValue,
            word: wordValue,
        })
            .then(afterConnectorSaved)
            .catch((err) => console.error(err));
    };

    const deleteConnector = (id) => {
        api.deleteConnector(id)
            .then(afterMutation)
            .catch((err) => {
                console.error(err);
                alert(
                    "Відповідь не можна видалити доки існують початі користувачами тести з використанням цієї відповіді. Ймовірне рішення видалити усі пройдені тести у яких користувач відповів цією відповіддю! "
                );
            });
    };

    const deleteQuestion = () => {
        api.deleteQuestion(item.id)
            .then(afterMutation)
            .catch((err) => {
                console.error(err);
                alert("Щось заважає видалити питання, можливо спочатку треба видалити усі поєднання (відповіді) з іншими питаннями");
            });
    };

    const trigerConnectorConfig = () => setConnectorConfig((prev) => !prev);

    const visibilityConnectorFields = (key) => {
        setConnectorFormEntities((prev) => ({
            ...prev,
            [key]: { ...prev[key], visible: !prev[key].visible },
        }));
    };

    const inputChangeHandler = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        // Any manual edit (typing, backspacing) detaches a linked word - the
        // text is no longer guaranteed to match that word once touched by
        // hand, so word_id goes back to null and the "tagged" look (driven
        // by wordValue being set) disappears on the very first keystroke.
        if (wordValue) {
            setWordValue("");
        }
        setIsVisibleChangeConnectorButton(newValue !== "" && !!choseNextQuestionMode.questionIdNext);
    };

    const toggleWordPicker = (e) => {
        if (e) e.stopPropagation();
        setIsWordPickerOpen((prev) => !prev);
    };
    const closeWordPicker = () => setIsWordPickerOpen(false);

    const handleWordPicked = (word) => {
        setWordValue(String(word.id));
        setInputValue(word.word);
        setIsWordPickerOpen(false);
        setIsVisibleChangeConnectorButton(!!(choseNextQuestionMode && choseNextQuestionMode.questionIdNext));
    };

    const clearWordHandler = () => {
        setWordValue("");
        setInputValue("");
    };

    const choseConnectorHandler = (id) => {
        setConnectorTypeChosen(id);
        setAddConnectorInfo(id === 1 ? CONNECTOR_INFO_BY_TYPE.text : CONNECTOR_INFO_BY_TYPE.button);
    };

    const checkboxConnectorHandler = (id) => setIsRightValue(id);
    const selectEsGroupChangeHandler = (e) => setEstimationGroupValue(e.target.value);
    const selectStageChangeHandler = (e) => setStageValue(e.target.value);
    const inputPointsChangeHandler = (e) => setPointValue(e.target.value);

    const showOptionsAddAnswerConnector = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isVisibleAddConnectorOptions) {
            setIsVisibleAddConnectorOptions(false);
            setChoseNextQuestionMode(null);
        } else {
            setIsVisibleAddConnectorOptions(true);
            setChoseNextQuestionMode({ questionIdFrom: item.id });
        }
    };

    const addLinkHandler = () => {
        setChoseNextQuestionMode((prev) => ({ ...prev, questionIdNext: item.id }));
    };

    const showHint = (connectorId, hintName) => setHintConnector({ connectorId, hintName });
    const clearHint = () => setHintConnector(undefined);

    const showConnectorFunctions = (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        setConnectorFunctionsToShowId((prev) => (prev ? null : id));
    };

    const deleteConnectorHandler = (e, id) => {
        if (!window.confirm("Чи напевно хочеш видалити цю відповідь ?")) return;
        deleteConnector(id);
    };

    const deleteQuestionHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm("Чи напевно хочеш видалити це питання ?")) return;
        deleteQuestion();
    };

    return {
        isVisibleAddConnectorOptions,
        inputValue,
        isRightValue,
        pointValue,
        connectorTypeChosen,
        addConnectorInfo,
        isVisibleChangeConnectorButton,
        connectorFormEntities,
        connectorConfig,
        connectorFunctionsToShowId,
        hintConnector,
        wordValue,
        isWordPickerOpen,
        trigerConnectorConfig,
        visibilityConnectorFields,
        inputChangeHandler,
        choseConnectorHandler,
        checkboxConnectorHandler,
        selectEsGroupChangeHandler,
        selectStageChangeHandler,
        inputPointsChangeHandler,
        toggleWordPicker,
        closeWordPicker,
        handleWordPicked,
        clearWordHandler,
        showOptionsAddAnswerConnector,
        addLinkHandler,
        showHint,
        clearHint,
        showConnectorFunctions,
        saveConnector,
        deleteConnectorHandler,
        deleteQuestionHandler,
    };
}
